import type { IFormGenContext, IFormGenSchema, TJSONSchema } from "@artifact-management/json-dynamic-form";
import { FormGenControlled } from "@complex/FormGen";
import { useFormGen, useParsedFormJsonSchema } from "@complex/FormGen/logics";
import { forwardRef, useImperativeHandle } from "react";
import type { FieldValues } from "react-hook-form";

const isSchemaObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const resolveEnumValue = (raw: unknown, propertySchema: unknown): unknown => {
  const enumOptions = isSchemaObject(propertySchema) ? propertySchema.enum : undefined;
  if (!Array.isArray(enumOptions)) {
    return raw;
  }
  const code = Array.isArray(raw) ? raw[0] : raw;
  if (typeof code !== "string") {
    return raw;
  }
  return enumOptions.find((option) => isSchemaObject(option) && option.value === code) ?? raw;
};

const pickSchemaFields = (item: FieldValues | undefined, jsonSchema: TJSONSchema): FieldValues => {
  if (!item) {
    return {};
  }
  const properties: Record<string, unknown> = jsonSchema.properties ?? {};
  const declared = Object.keys(properties);
  return Object.fromEntries(
    Object.entries(item)
      .filter(([key]) => declared.includes(key))
      .map(([key, value]) => [key, resolveEnumValue(value, properties[key])])
  );
};

export interface IMultiItemFormStepHandle {
  submit: (onValid: (data: FieldValues) => void) => void;
  getValues: () => FieldValues;
}

export interface IMultiItemFormStepProps {
  item: FieldValues | undefined;
  schema: IFormGenSchema;
  context?: IFormGenContext;
}

export const MultiItemFormStep = forwardRef<IMultiItemFormStepHandle, IMultiItemFormStepProps>(
  function MultiItemFormStep({ item, schema, context }, ref) {
    const parsedForDefaults = useParsedFormJsonSchema(schema, context);
    const { formControls, parsedJsonSchema } = useFormGen({
      schema,
      defaultValues: pickSchemaFields(item, parsedForDefaults),
      context,
    });

    useImperativeHandle(
      ref,
      () => ({
        submit: (onValid) => {
          void formControls.handleSubmit(onValid)();
        },
        getValues: () => formControls.getValues(),
      }),
      [formControls]
    );

    return (
      <FormGenControlled
        schema={schema}
        parsedJsonSchema={parsedJsonSchema}
        formControls={formControls}
        context={context}
      />
    );
  }
);
